import React, { useEffect, useState } from 'react';
import './NewHome.css';
import ChatAssistant from '../components/chat-assistant/ChatAssistant';
import VideoReels from './VideoReels';
import ZoneZappCard from './ZoneZappCard';

const API_BASE_URL = process.env.REACT_APP_MEDIA_API_URL || 'http://localhost:6002/api';
const CRM_API_BASE = process.env.REACT_APP_CRM_API_URL || 'http://localhost:6001/api';

export default function NewHome() {
  const [location, setLocation] = useState(null);
  const [cityName, setCityName] = useState('Your City');
  const [reels, setReels] = useState([]);
  const [events, setEvents] = useState([]);
  const [categoriesToday, setCategoriesToday] = useState([]);
  const [categoriesUpcoming, setCategoriesUpcoming] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [places, setPlaces] = useState([]);
  const [placesCategory, setPlacesCategory] = useState('schools');
  const [newsSummary, setNewsSummary] = useState(null);
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsExpanded, setNewsExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('home'); // 'home' or 'reels'
  const [selectedReelIndex, setSelectedReelIndex] = useState(0);
  const [currentPath] = useState(window.location.pathname);
  
  const isHomeActive = currentPath === '/webapp/home';
  const isMapActive = currentPath === '/webapp/map';

  const FIXED_PLACE_CATEGORIES = [
    { id: 'schools', label: 'Schools' },
    { id: 'hospitals', label: 'Hospitals' },
    { id: 'stores', label: 'Stores' },
    { id: 'fuel', label: 'Fuel' },
    { id: 'car_wash', label: 'Car Wash' },
    { id: 'sport', label: 'Sport' },
    { id: 'pharmacy', label: 'Pharmacy' },
    { id: 'restaurants', label: 'Restaurants' },
    { id: 'coffee', label: 'Coffee' },
    { id: 'shelter', label: 'Shelter' },
    { id: 'bicycle', label: 'Bicycle' },
    { id: 'trails', label: 'Trails' },
    { id: 'parks', label: 'Parks' },
    { id: 'playgrounds', label: 'Playgrounds' },
    { id: 'beach', label: 'Beach' },
    { id: 'water', label: 'Water' },
    { id: 'tourism', label: 'Tourism' },
    { id: 'attraction', label: 'Attraction' },
    { id: 'worship', label: 'Worship' }
  ];

  // Simple client-side cache using localStorage with TTL (30 minutes)
  const PLACES_CACHE_TTL = 60 * 30;
  const cacheKeyFor = (cat, lat, lon, radius) => `zz_places_cache:${cat}:${Math.round((lat||0)*10000)}:${Math.round((lon||0)*10000)}:${radius}`;
  
  const readCache = (key) => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const j = JSON.parse(raw);
      if (!j.ts || !j.data) return null;
      const age = (Date.now() - j.ts) / 1000;
      if (age > PLACES_CACHE_TTL) return null;
      return j.data;
    } catch (e) { return null; }
  };
  
  const writeCache = (key, data) => {
    try { localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data })); } catch (e) { /* ignore */ }
  };

  // Prefetch common categories on startup to make the UI snappy
  useEffect(() => {
    if (!location) return;
    const lat = location.latitude || location.lat;
    const lon = location.longitude || location.lon || location.lng;
    if (!lat || !lon) return;
    
    const prefetch = ['restaurants', 'coffee', 'stores', 'hospitals', 'parks', 'fuel', 'pharmacy'];
    prefetch.forEach(cat => {
      const key = cacheKeyFor(cat, lat, lon, 5000);
      const cached = readCache(key);
      if (!cached) {
        // Fetch and cache in background
        fetchPlaces(cat, location, true); // true = background fetch
      }
    });
  }, [location]);

  useEffect(() => {
    loadLocationAndData();
  }, []);

  const loadLocationAndData = async () => {
    try {
      // Get location from localStorage
      const locationData = localStorage.getItem('zz_location');
      if (locationData) {
        const loc = JSON.parse(locationData);
        setLocation(loc);
        setCityName(loc.city || 'Your City');
        
        // Fetch reels and events
        await Promise.all([
          fetchReels(loc),
          fetchEvents(loc),
          fetchPlaces('schools', loc),
          fetchNews(loc)
        ]);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReels = async (loc) => {
    try {
      let url = `${API_BASE_URL}/reels?limit=8`;
      if (loc?.latitude && loc?.longitude) {
        url += `&lat=${loc.latitude}&lng=${loc.longitude}`;
      }
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.success && data.data) {
        // Transform API data to match VideoReels component structure
        const transformedReels = data.data.map(reel => ({
          id: reel._id,
          type: reel.type,
          url: reel.url,
          thumbnail: reel.thumbnail,
          username: reel.username,
          location: reel.location?.name || reel.location || 'Unknown location',
          likes: reel.likesCount || reel.likes?.length || 0,
          caption: reel.caption || '',
          timestamp: new Date(reel.createdAt),
          userAvatar: reel.userAvatar,
          isLiked: false
        }));
        setReels(transformedReels);
      }
    } catch (error) {
      console.error('Error fetching reels:', error);
    }
  };

  const fetchEvents = async (loc) => {
    try {
      // Use the same endpoint as PostLoginHome
      let city = loc?.city || 'your area';
      const url = `${CRM_API_BASE.replace(/\/+$/, '')}/events?location=${encodeURIComponent(city)}&limit=60`;
      
      const headers = { Accept: 'application/json' };
      try {
        const tk = localStorage.getItem('zz_token');
        if (tk) headers['Authorization'] = `Bearer ${tk}`;
      } catch (e) {}
      
      const response = await fetch(url, { 
        headers, 
        credentials: 'include' 
      });
      
      if (!response.ok) {
        console.log('Events API returned error, using sample events');
        setSampleEvents();
        return;
      }
      
      const data = await response.json();
      const eventList = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
      
      if (eventList.length > 0) {
        // Group events by category like PostLoginHome (separate today and upcoming)
        const todayEvents = [];
        const upcomingEvents = [];
        const mapT = {};
        const mapU = {};
        
        eventList.forEach(ev => {
          const evDate = ev.start || ev.date || ev.timestamp || ev.createdAt;
          const cat = (ev.category || 'Other').toString();
          
          if (isToday(evDate)) {
            todayEvents.push(ev);
            (mapT[cat] = mapT[cat] || []).push(ev);
          } else {
            upcomingEvents.push(ev);
            (mapU[cat] = mapU[cat] || []).push(ev);
          }
        });
        
        // Sort upcoming by date
        Object.keys(mapU).forEach(k => 
          mapU[k].sort((a, b) => 
            new Date(a.start || a.date || a.timestamp || a.createdAt) - 
            new Date(b.start || b.date || b.timestamp || b.createdAt)
          )
        );
        
        // Create category lists
        const todayCats = Object.keys(mapT).map(k => ({
          category: k,
          count: mapT[k].length,
          events: mapT[k]
        }));
        
        const upcomingCats = Object.keys(mapU).map(k => ({
          category: k,
          count: mapU[k].length,
          events: mapU[k]
        }));
        
        setCategoriesToday(todayCats);
        setCategoriesUpcoming(upcomingCats);
        setEvents(todayEvents);
      } else {
        console.log('No events in API response, using sample events');
        setSampleEvents();
      }
    } catch (error) {
      console.log('Error fetching events, using sample events:', error.message);
      setSampleEvents();
    }
  };

  // Helper to check if date is today
  const isToday = (dateStr) => {
    if (!dateStr) return false;
    try {
      const d = new Date(dateStr);
      const now = new Date();
      return d.getDate() === now.getDate() && 
             d.getMonth() === now.getMonth() && 
             d.getFullYear() === now.getFullYear();
    } catch (e) {
      return false;
    }
  };

  const fetchPlaces = async (category, loc, isBackground = false) => {
    try {
      if (!loc?.latitude || !loc?.longitude) return;
      
      const radius = 5000;
      const key = cacheKeyFor(category, loc.latitude, loc.longitude, radius);
      
      // Check cache first
      const cached = readCache(key);
      if (cached && !isBackground) {
        setPlaces(cached.slice(0, 10));
        return;
      }
      
      // Correct endpoint structure: /api/places?lat=...&lon=...&category=...&radius=...
      const url = `${CRM_API_BASE.replace(/\/+$/, '')}/places?lat=${encodeURIComponent(loc.latitude)}&lon=${encodeURIComponent(loc.longitude)}&category=${encodeURIComponent(category)}&radius=${radius}&limit=12&excludeUnnamed=1`;
      
      const headers = { Accept: 'application/json' };
      try {
        const tk = localStorage.getItem('zz_token');
        if (tk) headers['Authorization'] = `Bearer ${tk}`;
      } catch (e) {}
      
      const response = await fetch(url, { 
        headers, 
        credentials: 'include' 
      });
      
      if (response.ok) {
        const data = await response.json();
        const placesList = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
        
        // Cache the results
        writeCache(key, placesList);
        
        // Update UI only if not background fetch
        if (!isBackground) {
          setPlaces(placesList.slice(0, 10)); // Limit to 10 places
        }
      }
    } catch (error) {
      console.log('Error fetching places:', error.message);
    }
  };

  const fetchNews = async (loc) => {
    try {
      // Check cache first
      const cached = localStorage.getItem('zz_news_summary');
      if (cached) {
        setNewsSummary(cached);
        return;
      }
    } catch (e) {}

    setNewsLoading(true);
    try {
      if (!loc?.latitude || !loc?.longitude) {
        setNewsLoading(false);
        return;
      }

      const url = `${CRM_API_BASE.replace(/\/+$/, '')}/news?lat=${loc.latitude}&lon=${loc.longitude}`;
      
      const headers = { Accept: 'application/json' };
      try {
        const tk = localStorage.getItem('zz_token');
        if (tk) headers['Authorization'] = `Bearer ${tk}`;
      } catch (e) {}
      
      const response = await fetch(url, {
        headers,
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const text = data?.summary || data?.data || (typeof data === 'string' ? data : null);
        
        if (text) {
          const asString = typeof text === 'string' ? text : JSON.stringify(text);
          setNewsSummary(asString);
          try {
            localStorage.setItem('zz_news_summary', asString);
          } catch (e) {}
        }
      }
    } catch (error) {
      console.log('Error fetching news:', error.message);
    } finally {
      setNewsLoading(false);
    }
  };

  const setSampleEvents = () => {
    setEvents([
      {
        _id: '1',
        title: 'Concert in the Park',
        description: 'Live music under the stars',
        category: ['Music', 'Community'],
        image: 'https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?w=800',
        attending: ['Going', 'Interested']
      },
      {
        _id: '2',
        title: 'Food Festival',
        description: 'Taste local flavors',
        category: ['Food'],
        image: 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=800',
        attending: []
      }
    ]);
  };

  const navigateToReels = (reelIndex = 0) => {
    setSelectedReelIndex(reelIndex);
    setViewMode('reels');
  };

  const navigateToEvents = () => {
    window.location.href = '/webapp/home/full'; // Full home with events and places
  };

  const navigateToChat = () => {
    window.location.href = '/webapp/home/full'; // Full home with chat assistant
  };

  const handleBackFromReels = () => {
    setViewMode('home');
  };

  const handleCategoryClick = (category, source) => {
    setSelectedCategory({ ...category, source });
  };

  const handleEventClick = async (ev) => {
    // Track view
    await trackView(ev.id || ev._id);
    // Open event details (you can customize this)
    window.open(ev.link || ev.url || '#', '_blank');
  };

  const trackView = async (id) => {
    if (!id) return;
    try {
      const url = `${CRM_API_BASE.replace(/\/+$/, '')}/events/${encodeURIComponent(id)}/view`;
      await fetch(url, { method: 'POST', credentials: 'include', headers: { Accept: 'application/json' } });
    } catch (e) { /* ignore */ }
  };

  const getEventImage = (ev) => {
    return ev.image || ev.imageUrl || ev.img || null;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      if (d.toDateString() === today.toDateString()) return 'Today';
      if (d.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
      
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch (e) {
      return '';
    }
  };

  const shortDesc = (text, maxLen = 120) => {
    if (!text) return '';
    if (text.length <= maxLen) return text;
    return text.substring(0, maxLen).trim() + '…';
  };

  // Reel categories based on actual reel types
  const getReelThumbnail = (index) => {
    if (reels.length === 0) return null;
    // Cycle through available reels if we have fewer than 4
    const reel = reels[index % reels.length];
    // For videos, use thumbnail, for photos use the url directly
    return reel?.thumbnail || reel?.url || null;
  };

  // Generate reel categories dynamically based on available reels
  const reelCategories = reels.length > 0 
    ? Array.from({ length: Math.min(4, reels.length) }, (_, i) => ({
        id: `reel-${i}`,
        label: reels[i]?.caption?.substring(0, 20) || `Reel ${i + 1}`,
        index: i
      }))
    : [
        { id: 'city-hall', label: 'City Hall', index: 0 },
        { id: 'downtown', label: 'Downtown', index: 1 },
        { id: 'trails', label: 'Trail Views', index: 2 },
        { id: 'eats', label: 'Local Eats', index: 3 },
      ];

  // Show reels view if viewMode is 'reels'
  if (viewMode === 'reels') {
    return (
      <VideoReels 
        initialIndex={selectedReelIndex} 
        initialReels={reels}
        onBack={handleBackFromReels}
      />
    );
  }

  return (
    <div className="new-home-container">
      <ChatAssistant />
      {/* Hero Section with Background */}
      <div className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title">Welcome to {cityName}</h1>
          <p className="hero-subtitle">Discover what's happening around you.</p>
          
          <div className="hero-actions">
            <button className="btn-primary" onClick={navigateToChat}>
              Connect with Locals
            </button>
            <button className="btn-secondary" onClick={navigateToEvents}>
              Visitor Info
            </button>
          </div>
        </div>
      </div>

      {/* Reels Section */}
      <section className="reels-section">
        <div className="section-header">
          <h2>Reels</h2>
        </div>
        
        <div className="reels-categories">
          {reelCategories.map(category => {
            const thumbnail = getReelThumbnail(category.index);
            return (
              <div 
                key={category.id} 
                className="reel-category-card" 
                onClick={() => navigateToReels(category.index)}
              >
                <div 
                  className="reel-category-image"
                  style={{
                    backgroundImage: thumbnail 
                      ? `url(${thumbnail})`
                      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  }}
                >
                  {!thumbnail && (
                    <div className="category-placeholder">
                      <span>📹</span>
                    </div>
                  )}
                </div>
                <h3 className="category-label">{category.label}</h3>
              </div>
            );
          })}
        </div>
      </section>

      {/* News Section */}
      <section className="news-section">
        <div className="section-header">
          <h2>Local News</h2>
          {newsSummary && (
            <button 
              className="refresh-btn"
              onClick={() => {
                try {
                  localStorage.removeItem('zz_news_summary');
                  setNewsSummary(null);
                  setNewsExpanded(false);
                  fetchNews(location);
                } catch (e) {}
              }}
            >
              🔄 Refresh
            </button>
          )}
        </div>
        
        {newsLoading ? (
          <div className="loading-message">Loading news…</div>
        ) : newsSummary ? (
          <div className="news-content">
            <div 
              className={`news-summary ${newsExpanded ? 'expanded' : ''}`}
              dangerouslySetInnerHTML={{ __html: newsSummary }}
            />
            <button 
              className="expand-btn"
              onClick={() => setNewsExpanded(!newsExpanded)}
            >
              {newsExpanded ? 'Show Less' : 'Read More'}
            </button>
          </div>
        ) : (
          <div className="no-news">No news available for your area yet.</div>
        )}
      </section>

      {/* What's On Today Section */}
      <section className="whats-on-section">
        <h2 className="section-title">What's On Today in {cityName}</h2>
        
        {!selectedCategory ? (
          <>
            {/* Today's Events */}
            {categoriesToday.length > 0 ? (
              <div className="events-subsection">
                <h3 className="subsection-title">What's to do today</h3>
                <div className="event-categories">
                  {categoriesToday.map(cat => (
                    <button 
                      key={cat.category} 
                      className="category-chip"
                      onClick={() => handleCategoryClick(cat, 'today')}
                    >
                      {cat.category} ({cat.count})
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            
            {/* Upcoming Events */}
            {categoriesUpcoming.length > 0 ? (
              <div className="events-subsection">
                <h3 className="subsection-title">What's happening soon</h3>
                <div className="event-categories">
                  {categoriesUpcoming.map(cat => (
                    <button 
                      key={cat.category} 
                      className="category-chip"
                      onClick={() => handleCategoryClick(cat, 'upcoming')}
                    >
                      {cat.category} ({cat.count})
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="events-grid">
                <div className="event-card" onClick={navigateToEvents}>
                  <div 
                    className="event-image"
                    style={{
                      backgroundImage: 'url(https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?w=800)'
                    }}
                  ></div>
                  <div className="event-content">
                    <h3 className="event-title">Concert in the Park</h3>
                    <div className="event-tags">
                      <span className="event-tag">Music</span>
                      <span className="event-tag">Community</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="category-detail">
            <div className="category-detail-header">
              <button className="back-btn" onClick={() => setSelectedCategory(null)}>
                ← Back
              </button>
              <h3 className="category-detail-title">
                {selectedCategory.category} ({selectedCategory.count})
              </h3>
            </div>
            <div className="category-events">
              {selectedCategory.events && selectedCategory.events.length > 0 ? 
                selectedCategory.events.map(ev => (
                  <article 
                    key={ev.id || ev._id || ev.title} 
                    className="event-detail-card"
                    onClick={() => handleEventClick(ev)}
                  >
                    {getEventImage(ev) && (
                      <div 
                        className="event-detail-image"
                        style={{
                          backgroundImage: `url(${getEventImage(ev)})`
                        }}
                      ></div>
                    )}
                    <div className="event-detail-content">
                      <h3 className="event-detail-title">{ev.title || ev.name}</h3>
                      <div className="event-detail-meta">
                        {ev.venueAddress || ev.venue || ev.location || ''} • {formatDate(ev.start || ev.date || ev.timestamp)}
                      </div>
                      {ev.description && (
                        <p className="event-detail-desc">{shortDesc(ev.description)}</p>
                      )}
                      <div className="event-detail-stats">
                        <span className="stat">❤️ {ev.likes || 0}</span>
                        <span className="stat">👁️ {ev.views || 0}</span>
                        <span className="stat">🔗 {ev.clicks || 0}</span>
                      </div>
                    </div>
                  </article>
                )) : (
                  <div className="no-events">No events in this category.</div>
                )
              }
            </div>
          </div>
        )}
      </section>

      {/* Zone's Zapp Analytics Card */}
      {location && <ZoneZappCard location={location} />}

      {/* Places Section */}
      <section className="places-section">
        <h2 className="section-title">Places</h2>
        
        <div className="place-categories">
          {FIXED_PLACE_CATEGORIES.map(cat => (
            <button
              key={cat.id}
              className={`category-chip ${placesCategory === cat.id ? 'active' : ''}`}
              onClick={() => {
                // Store selected category for map page
                try {
                  localStorage.setItem('zz_map_category', cat.id);
                } catch (e) {}
                
                // Navigate to map page
                window.location.href = '/webapp/map';
              }}
            >
              {cat.label}
            </button>
          ))}
        </div>
        
        {places.length > 0 ? (
          <div className="places-list">
            {places.slice(0, 3).map((place, idx) => (
              <div key={idx} className="place-chip">
                📍 {place.name || place.title || 'Unknown Place'}
              </div>
            ))}
          </div>
        ) : (
          <div className="no-places">No places found nearby</div>
        )}
      </section>

      {/* Bottom Navigation Menu */}
      <nav style={{ position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: 12, zIndex: 9999, width: 480, maxWidth: '95%', display: 'flex', justifyContent: 'center' }} aria-label="Primary">
        <div style={{ background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 249, 252, 0.98) 100%)', borderRadius: 28, boxShadow: '0 8px 32px rgba(102, 126, 234, 0.2), 0 2px 8px rgba(0,0,0,0.08)', display: 'flex', padding: 8, width: '100%', alignItems: 'center', justifyContent: 'space-around', backdropFilter: 'blur(10px)', border: '2px solid rgba(102, 126, 234, 0.1)' }}>
          <button onClick={() => { setViewMode('home'); }} style={{ background: (isHomeActive && viewMode === 'home') ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent', border: 'none', color: (isHomeActive && viewMode === 'home') ? '#fff' : '#64748b', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 14px', cursor: 'pointer', borderRadius: 16, minWidth: 70, transition: 'all 0.3s ease', fontWeight: 600, fontSize: 13 }} aria-label="Home">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M3 11.5L12 4l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1v-8.5z" fill="currentColor"/></svg>
            <div style={{ marginTop: 4 }}>Home</div>
          </button>
          
          <button onClick={() => { setViewMode('reels'); }} style={{ background: viewMode === 'reels' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent', border: 'none', color: viewMode === 'reels' ? '#fff' : '#64748b', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 14px', cursor: 'pointer', borderRadius: 16, minWidth: 70, transition: 'all 0.3s ease', fontWeight: 600, fontSize: 13 }} aria-label="Reels">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" fill="currentColor"/></svg>
            <div style={{ marginTop: 4 }}>Reels</div>
          </button>

          <button onClick={() => { window.location.href = '/webapp/map'; }} style={{ background: isMapActive ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent', border: 'none', color: isMapActive ? '#fff' : '#64748b', borderRadius: 16, padding: '12px 20px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.3s ease', fontSize: 14 }} aria-label="Map">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="currentColor"/></svg>
            Map
          </button>

          <button onClick={() => { window.location.href = '/webapp/home/full'; }} style={{ background: 'transparent', border: 'none', color: '#64748b', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 14px', cursor: 'pointer', borderRadius: 16, minWidth: 70, transition: 'all 0.3s ease', fontWeight: 600, fontSize: 13 }} aria-label="Locals">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10zm0 2c-5 0-9 2.5-9 5v1h18v-1c0-2.5-4-5-9-5z" fill="currentColor"/></svg>
            <div style={{ marginTop: 4 }}>More</div>
          </button>
        </div>
      </nav>
    </div>
  );
}
